/** Formal source slices are distinct from quote-local price overrides. */
export const QUOTE_SOURCE_FRAGMENT_VERSION =
  'quote-source-fragment-v1' as const;

export interface QuoteSourceFragment {
  version: typeof QUOTE_SOURCE_FRAGMENT_VERSION;
  mode: 'complete' | 'quote-customized' | 'receipt-detached';
  instanceKey: string;
  fragmentKey: string;
  memberKey: string;
  instanceQuantity: number;
  origin?: {
    quoteId: string;
    quoteVersionId: string;
    quoteItemId: string;
    sourceHash: string;
  };
  /** Read-only proof of an already materialized detached row, issued by the API. */
  detachedFrom?: {
    quoteId: string;
    quoteVersionId: string;
    quoteItemId: string;
  };
  /** Read-only provenance for copying a stored row while new matching is disabled. */
  copyFrom?: {
    quoteId: string;
    quoteVersionId: string;
    quoteItemId: string;
  };
}

export function isCompleteQuoteSourceFragment(
  fragment: Pick<QuoteSourceFragment, 'mode'> | null | undefined,
): boolean {
  return fragment?.mode === 'complete' || fragment?.mode === 'quote-customized';
}

export function usesQuoteFragmentAllocation(
  fragment: Pick<QuoteSourceFragment, 'mode'> | null | undefined,
): boolean {
  return fragment?.mode !== 'quote-customized';
}

export interface QuoteFragmentAllocation {
  version: 'quantity-cent-v1';
  instanceKey: string;
  fragmentKey: string;
  relationKey: string;
  relationQuantity: number;
  wholeQuantity: number;
  wholeRentalLineCents: number;
  allocatedRentalLineCents: number;
  allocatedPackageOriginalLineCents?: number;
  sourceExclusiveDiscountPercent?: string | null;
}

export interface FragmentSourceIdentity {
  sourceType: string;
  sourceRevisionId?: string;
  packageRelationId?: string;
  planPackageMembershipId?: string;
  formalPlanOccurrence?: {
    occurrenceKey: string;
    itemKey: string;
    originPlanRevisionId?: string;
  };
}

export function fragmentMemberKey(item: FragmentSourceIdentity): string {
  if (!item.sourceRevisionId) throw new Error('分行来源缺少准确修订');
  if (
    item.sourceType === 'package' &&
    item.formalPlanOccurrence?.originPlanRevisionId
  )
    return `package:${item.sourceRevisionId}:origin:${item.formalPlanOccurrence.originPlanRevisionId}:occurrence:${item.formalPlanOccurrence.occurrenceKey}:item:${item.formalPlanOccurrence.itemKey}`;
  if (item.sourceType === 'package' && item.packageRelationId)
    return `package:${item.sourceRevisionId}:relation:${item.packageRelationId}`;
  if (item.sourceType === 'plan' && item.formalPlanOccurrence)
    return `plan:${item.sourceRevisionId}:occurrence:${item.formalPlanOccurrence.occurrenceKey}:item:${item.formalPlanOccurrence.itemKey}`;
  if (
    item.sourceType === 'plan' &&
    item.planPackageMembershipId &&
    item.packageRelationId
  )
    return `plan:${item.sourceRevisionId}:membership:${item.planPackageMembershipId}:relation:${item.packageRelationId}`;
  throw new Error('分行缺少准确套餐或方案成员身份');
}

export function assertSourceFragment(
  value: unknown,
): asserts value is QuoteSourceFragment {
  if (!value || typeof value !== 'object') throw new Error('分行来源合同缺失');
  const fragment = value as Partial<QuoteSourceFragment>;
  if (
    fragment.version !== QUOTE_SOURCE_FRAGMENT_VERSION ||
    !['complete', 'quote-customized', 'receipt-detached'].includes(
      fragment.mode ?? '',
    ) ||
    !Number.isSafeInteger(fragment.instanceQuantity) ||
    (fragment.instanceQuantity ?? 0) < 1 ||
    [fragment.instanceKey, fragment.fragmentKey].some(
      (key) => typeof key !== 'string' || !/^[\x21-\x7e]{1,256}$/.test(key),
    ) ||
    typeof fragment.memberKey !== 'string' ||
    !/^[\x21-\x7e]{1,512}$/.test(fragment.memberKey)
  )
    throw new Error('分行来源合同版本或身份无效');
  if (
    fragment.mode === 'complete' &&
    (fragment.origin !== undefined || fragment.detachedFrom !== undefined)
  )
    throw new Error('完整实例不能声明回执拆分来源');
  if (
    fragment.mode === 'quote-customized' &&
    (fragment.origin !== undefined ||
      fragment.detachedFrom !== undefined ||
      fragment.copyFrom !== undefined)
  )
    throw new Error('报价自定义实例不能声明其他来源证明');
  if (
    fragment.mode === 'quote-customized' &&
    (!fragment.instanceKey || !fragment.fragmentKey || !fragment.memberKey)
  )
    throw new Error('报价自定义实例缺少成员身份');
  const validId = (value: unknown) =>
    typeof value === 'string' && /^[1-9][0-9]{0,18}$/.test(value);
  if (
    fragment.copyFrom !== undefined &&
    (!fragment.copyFrom ||
      [
        fragment.copyFrom.quoteId,
        fragment.copyFrom.quoteVersionId,
        fragment.copyFrom.quoteItemId,
      ].some((value) => !validId(value)))
  )
    throw new Error('分行复制来源证明无效');
  if (
    fragment.mode === 'receipt-detached' &&
    (!fragment.origin ||
      [
        fragment.origin.quoteId,
        fragment.origin.quoteVersionId,
        fragment.origin.quoteItemId,
      ].some((value) => !validId(value)) ||
      !/^[a-f0-9]{64}$/.test(fragment.origin.sourceHash))
  )
    throw new Error('回执分行缺少不可变发布来源');
  if (
    fragment.mode === 'receipt-detached' &&
    (!fragment.detachedFrom ||
      [
        fragment.detachedFrom.quoteId,
        fragment.detachedFrom.quoteVersionId,
        fragment.detachedFrom.quoteItemId,
      ].some((value) => !validId(value)))
  )
    throw new Error('回执分行缺少服务端已存行证明');
}

export function assertFragmentAllocation(
  value: unknown,
  fragment: QuoteSourceFragment,
  quantity: number,
): asserts value is QuoteFragmentAllocation {
  assertSourceFragment(fragment);
  if (!value || typeof value !== 'object')
    throw new Error('分行缺少正式金额合同');
  const allocation = value as Partial<QuoteFragmentAllocation>;
  const safeMoney = (amount: unknown) =>
    typeof amount === 'number' && Number.isSafeInteger(amount) && amount >= 0;
  if (
    allocation.version !== 'quantity-cent-v1' ||
    allocation.instanceKey !== fragment.instanceKey ||
    allocation.fragmentKey !== fragment.fragmentKey ||
    allocation.relationKey !== fragment.memberKey ||
    !Number.isSafeInteger(quantity) ||
    quantity < 1 ||
    !Number.isSafeInteger(allocation.relationQuantity) ||
    (allocation.relationQuantity ?? 0) < 1 ||
    !Number.isSafeInteger(allocation.wholeQuantity) ||
    (allocation.wholeQuantity ?? 0) < quantity ||
    allocation.wholeQuantity !==
      (allocation.relationQuantity ?? 0) * fragment.instanceQuantity ||
    !safeMoney(allocation.wholeRentalLineCents) ||
    !safeMoney(allocation.allocatedRentalLineCents) ||
    (allocation.allocatedRentalLineCents ?? 0) >
      (allocation.wholeRentalLineCents ?? 0) ||
    (allocation.allocatedPackageOriginalLineCents !== undefined &&
      !safeMoney(allocation.allocatedPackageOriginalLineCents)) ||
    (allocation.sourceExclusiveDiscountPercent !== undefined &&
      allocation.sourceExclusiveDiscountPercent !== null &&
      (typeof allocation.sourceExclusiveDiscountPercent !== 'string' ||
        !/^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,18})?$/.test(
          allocation.sourceExclusiveDiscountPercent,
        ) ||
        !Number.isFinite(Number(allocation.sourceExclusiveDiscountPercent))))
  )
    throw new Error('分行金额合同版本、身份或数量无效');
}

/** Integer largest remainder; ASCII fragment keys provide a persistent tie break. */
export function allocateFragmentCents(
  total: number,
  parts: readonly { key: string; quantity: number }[],
): Map<string, number> {
  if (
    !Number.isSafeInteger(total) ||
    total < 0 ||
    parts.length === 0 ||
    new Set(parts.map((part) => part.key)).size !== parts.length ||
    parts.some(
      (part) => !Number.isSafeInteger(part.quantity) || part.quantity < 1,
    )
  )
    throw new Error('分行数量或金额无效');
  const denominator = parts.reduce(
    (sum, part) => sum + BigInt(part.quantity),
    0n,
  );
  const amounts = parts.map((part) => {
    const numerator = BigInt(total) * BigInt(part.quantity);
    return {
      key: part.key,
      amount: Number(numerator / denominator),
      remainder: numerator % denominator,
    };
  });
  let remaining = total - amounts.reduce((sum, part) => sum + part.amount, 0);
  amounts.sort((left, right) =>
    left.remainder > right.remainder
      ? -1
      : left.remainder < right.remainder
        ? 1
        : left.key < right.key
          ? -1
          : left.key > right.key
            ? 1
            : 0,
  );
  for (const part of amounts) {
    if (remaining <= 0) break;
    part.amount += 1;
    remaining -= 1;
  }
  return new Map(amounts.map((part) => [part.key, part.amount]));
}

export function coefficientExclusivePercent(coefficient: string): string {
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,18})?$/.test(coefficient))
    throw new Error('方案正式系数无效');
  const [whole, fraction = ''] = coefficient.split('.');
  const scale = 10n ** BigInt(fraction.length);
  const numerator = BigInt(`${whole}${fraction}`) * 100n;
  const remainder = numerator % scale;
  return remainder === 0n
    ? String(numerator / scale)
    : `${numerator / scale}.${String(remainder).padStart(fraction.length, '0').replace(/0+$/, '')}`;
}
